import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'ng-table',
  templateUrl: './ng-table.component.html',
  styleUrls: ['./ng-table.component.css']
})
export class NgTableComponent implements OnInit {

  // Table values
  @Input() public rows: Array<any> = [];

  @Input() set data(data: Array<any>) {
    this._data = data;
    this.onChangeTable(this.config);
  }

  @Input()
  public set config(conf: any) {
    if (!conf.className) {
      conf.className = 'table-striped table-bordered';
    }
    if (conf.className instanceof Array) {
      conf.className = conf.className.join(' ');
    }
    this._config = conf;
  }

  @Input()
  public showResetFiltersButton: Boolean = false;

  // Outputs (Events)
  @Output() public tableChanged: EventEmitter<any> = new EventEmitter();
  @Output() public cellClicked: EventEmitter<any> = new EventEmitter();
  @Output() public filtersReseted: EventEmitter<any> = new EventEmitter();

  public showFilterRow: Boolean = false;
  public selectedRow: number = -1;

  @Input()
  public set columns(values: Array<any>) {
    values.forEach((value: any) => {
      if (value.filtering) {
        this.showFilterRow = true;
      }
      if (value.className && value.className instanceof Array) {
        value.className = value.className.join(' ');
      }
      let column = this._columns.find((col: any) => col.name === value.name);
      if (column) {
        let index = this._columns.indexOf(column);
        this._columns[index] = Object.assign({}, column, value);
      }
      if (!column) {
        this._columns.push(value);
      }
    });
    this.onChangeTable(this.config);
  }

  private _columns: Array<any> = [];
  private _config: any = {};
  private _data: Array<any> = [];

  // Paging
  public page: number = 1;
  public itemsPerPage: number = 10;
  public maxSize: number = 5;
  public numPages: number = 0;
  public length: number = 0;

  // Internal
  private filteredSortedData: Array<any> = [];

  public constructor(private sanitizer: DomSanitizer) {
  }
  
  public ngOnInit(): void {
    //this.onChangeTable(this.config);
  }

  public sanitize(html: string): SafeHtml {
    if (html === undefined)
      html = '';

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public get columns(): Array<any> {
    return this._columns;
  }

  public get config(): any {
    return this._config;
  }

  public get configColumns(): any {
    let sortColumns: Array<any> = [];

    this.columns.forEach((column: any) => {
      if (column.sort) {
        sortColumns.push(column);
      }
    });

    return { columns: sortColumns };
  }

  public changePage(page: any, data: Array<any> = this.data): Array<any> {
    let start = (page.page - 1) * page.itemsPerPage;
    let end = page.itemsPerPage > -1 ? (start + page.itemsPerPage) : data.length;
    return data.slice(start, end);
  }

  public changeSort(data: any, config: any): any {
    if (!config.sorting) {
      return data;
    }

    let columns = this.config.sorting.columns || [];
    let columnName: string = void 0;
    let columnType: string = void 0;
    let sort: string = void 0;

    for (let i = 0; i < columns.length; i++) {
      if (columns[i].sort !== '' && columns[i].sort !== false) {
        columnName = columns[i].name;
        columnType = columns[i].type;
        sort = columns[i].sort;
      }
    }

    if (!columnName) {
      return data;
    }

    // simple sorting
    return data.sort((previous: any, current: any) => {
      let first = this.getData(previous, columnName);
      let second = this.getData(current, columnName);

      first = first && typeof (first === 'string') ? first.replace(/<[^>]*>?/gm, '') : first;
      second = second && typeof (second === 'string') ? second.replace(/<[^>]*>?/gm, '') : second;

      if (columnType === 'date') {
        first = Date.parse(first);
        second = Date.parse(second);
      }

      if (first > second) {
        return sort === 'desc' ? -1 : 1;
      } else if (first < second) {
        return sort === 'asc' ? -1 : 1;
      }
      return 0;
    });
  }

  public changeFilter(data: any, config: any): any {
    let filteredData: Array<any> = data;
    this.columns.forEach((column: any) => {
      if (column.filtering) {
        if (!column.filtering.filterString) {
          if (column.filtering.filterEmpty)
            return filteredData = filteredData.filter((item: any) => this.getData(item, column.name));
          return true;
        }
        switch (column.filtering.filterType) {
          case 'text':
          case 'select':
            if (column.filtering.filterFn) {
              filteredData = column.filtering.filterFn(filteredData, column.filtering.filterString);  
            } else {
              filteredData = filteredData.filter((item: any) => {
                let propertyName = column.filtering.columnName ? column.filtering.columnName : column.name;
                let value = this.getData(item, propertyName);
                return value ? value.toLowerCase().match(column.filtering.filterString.toLowerCase()) : false;
              });
            }
            break;
          case 'daterange':
            filteredData = filteredData.filter((item: any) => {
              const date = Date.parse(this.getData(item, column.name));
              const retVal = date >= column.filtering.filterString[0] && date < column.filtering.filterString[1];
              return retVal;
            });
            break;
        }
      }
    });

    if (!config.filtering) {
      return filteredData;
    }

    if (config.filtering.columnName) {
      return filteredData.filter((item: any) =>
        this.getData(item, config.filtering.columnName).toLowerCase().match(this.config.filtering.filterString.toLowerCase()));
    }

    let tempArray: Array<any> = [];
    filteredData.forEach((item: any) => {
      let flag = false;
      this.columns.forEach((column: any) => {
        let value = this.getData(item, column.name);
        if (value) {
          if (value.toLowerCase().match(this.config.filtering.filterString.toLowerCase())) {
            flag = true;
          }
        }
      });
      if (flag) {
        tempArray.push(item);
      }
    });
    filteredData = tempArray;

    return filteredData;
  }

  public onResetDateRangeFilter(column: any) {
    column.filtering.bsRangeValue = null;
    this.onChangeTable(this.config);
  }

  public onChangeTableSort(column: any): void {
    this._columns.forEach((col: any) => {
      if (col.name !== column.name && col.sort !== false) {
        col.sort = '';
      }
    });

    this.onChangeTable({ sorting: this.configColumns });
  }

  public onChangeTable(config: any, page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): void {

    if (config.name != undefined) {
      this._columns.forEach((col: any) => {
        if (col.name !== config.name && col.sort !== false) {
          col.sort = '';
        }
      });
    }

    if (this._config.filtering) {
      Object.assign(this.config.filtering, this._config.filtering);
    }

    if (this._config.sorting) {
      Object.assign(this.config.sorting, this._config.sorting);
    }

    let filteredData = this.changeFilter(this._data, this._config);
    let sortedData = this.changeSort(filteredData, this._config);

    this.rows = this.page && this._config.paging ? this.changePage(page, sortedData) : sortedData;
    this.length = sortedData.length;

    this.filteredSortedData = sortedData;
    this.tableChanged.emit(sortedData);
  }

  public getData(row: any, propertyName: string): any {
    return propertyName.split('.').reduce((prev: any, curr: string) => prev[curr], row);
  }

  public cellClick(row: any, column: any): void {
    this.cellClicked.emit({ row, column });
  }

  public onAllSelectionClick(event: any): void {
    this.filteredSortedData.forEach(item => item.selected = event.target.checked);
    this.tableChanged.emit(this.filteredSortedData);
  }

  public itemModelChange(event: any): void {
    this.tableChanged.emit(this.filteredSortedData);
  }

  public onResetFilters() {
    let changed = false;
    this.columns = this.columns.map(column => {
      if (column.filtering.filterString) {
        let filtering = {...column.filtering, filterString: ''};
        if (filtering.bsRangeValue) filtering.bsRangeValue = null;
        column.filtering = filtering;
        changed = true;
      }
      return column;
    });
    if (changed) {
      this.filtersReseted.emit(this.columns);
    }
  }

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    switch (event.keyCode) {
      case 38:
        if (this.selectedRow > 0) {
          this.selectedRow--;
        }
        break;
      case 40:
        if (this.selectedRow < this._data.length--) {
          this.selectedRow++;
        }
        break;
    }
  }

}



/** WEBPACK FOOTER **
 ** ./components/table/ng-table.component.ts
 **/